import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";

import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useAuth } from "../context/AuthProvider";

const poppins = { fontFamily: "'Poppins', sans-serif" };

const menuIcons = {
  Dashboard: <SpaceDashboardIcon />,
  Sales: <ReceiptLongIcon />,
  User: <ManageAccountsIcon />,
  "Door Prize": <EmojiEventsIcon />,
  "Grand Prize": <MilitaryTechIcon />,
};

export default function Header({ user }) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [activePage, setActivePage] = useState("");
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const menu = [
    "Dashboard",
    "Sales",
    "User",
    "Door Prize",
    "Grand Prize",
    "doorprizesesi1",
    "doorprizesesi2",
  ];

  useEffect(() => {
    setActivePage(location.pathname.substring(1));
  }, [location.pathname]);

  const handleItemClick = (page) => {
    setIsDrawerOpen(false);
    navigate(`/${page.replace(/\s+/g, "").toLowerCase()}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openProfileMenu = Boolean(profileAnchorEl);

  const isAdmin = user.isAdmin === 1 || user.isSuperAdmin === 1;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      px={2}
      height={64}
      sx={{
        zIndex: 10,
        position: "relative",
        borderBottom: isAdmin ? "1px solid rgba(255,255,255,0.07)" : "none",
        backgroundColor: isAdmin ? "rgba(5,13,31,0.6)" : "transparent",
        backdropFilter: isAdmin ? "blur(10px)" : "none",
      }}
    >
      {user.isAdmin === 1 || user.isSuperAdmin === 1 ? (
        <>
          {/* ── DRAWER ─────────────────────────────────────────────── */}
          <Drawer
            anchor="left"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: 260,
                background: "linear-gradient(180deg, #091b3d 0%, #060f24 100%)",
                borderRight: "1px solid rgba(255,255,255,0.08)",
              },
            }}
          >
            {/* Drawer header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2.5}
              sx={{
                height: 64,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <img
                  src="Theme Title.png"
                  alt="logo"
                  style={{ height: 36, objectFit: "contain" }}
                />
              </Box>
              <IconButton
                onClick={() => setIsDrawerOpen(false)}
                sx={{ color: "rgba(186,230,253,0.6)", p: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Admin badge */}
            <Box px={2.5} py={2}>
              <Box
                sx={{
                  backgroundColor: "rgba(37,99,235,0.15)",
                  border: "1px solid rgba(96,165,250,0.25)",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.7rem",
                    color: "rgba(186,230,253,0.5)",
                    fontWeight: 500,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  Panel
                </Typography>
                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.85rem",
                    color: "rgba(147,197,253,0.9)",
                    fontWeight: 700,
                  }}
                >
                  Administrator
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mx: 2 }} />

            {/* Menu items */}
            <List sx={{ px: 1.5, pt: 1 }}>
              {menu.map((menuItem) => {
                const pageKey = menuItem.replace(/\s+/g, "").toLowerCase();
                const isActive = activePage === pageKey;
                return (
                  <ListItem key={menuItem} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleItemClick(menuItem)}
                      disabled={isActive}
                      sx={{
                        borderRadius: "12px",
                        px: 2,
                        py: 1.2,
                        backgroundColor: isActive
                          ? "rgba(37,99,235,0.2)"
                          : "transparent",
                        border: isActive
                          ? "1px solid rgba(96,165,250,0.3)"
                          : "1px solid transparent",
                        "&:hover": {
                          backgroundColor: "rgba(37,99,235,0.1)",
                          border: "1px solid rgba(96,165,250,0.2)",
                        },
                        "&.Mui-disabled": {
                          opacity: 1,
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isActive
                            ? "rgba(96,165,250,0.95)"
                            : "rgba(186,230,253,0.5)",
                        }}
                      >
                        {menuIcons[menuItem] ?? <DarkModeOutlinedIcon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={menuItem}
                        primaryTypographyProps={{
                          sx: {
                            ...poppins,
                            fontSize: "0.88rem",
                            fontWeight: isActive ? 700 : 500,
                            color: isActive
                              ? "rgba(147,197,253,0.95)"
                              : "rgba(186,230,253,0.65)",
                          },
                        }}
                      />
                      {isActive && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: "#3b82f6",
                            boxShadow: "0 0 6px rgba(59,130,246,0.8)",
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>

            {/* Bottom logout */}
            <Box sx={{ mt: "auto", px: 2.5, pb: 3 }}>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mb: 2 }} />
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: "12px",
                  px: 2,
                  py: 1.2,
                  border: "1px solid rgba(248,113,113,0.2)",
                  "&:hover": { backgroundColor: "rgba(239,68,68,0.1)" },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 36, color: "rgba(248,113,113,0.7)" }}
                >
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    sx: {
                      ...poppins,
                      fontSize: "0.88rem",
                      fontWeight: 500,
                      color: "rgba(248,113,113,0.8)",
                    },
                  }}
                />
              </ListItemButton>
            </Box>
          </Drawer>

          <IconButton
            onClick={() => setIsDrawerOpen(true)}
            sx={{ color: "rgba(186,230,253,0.85)" }}
          >
            <MenuIcon />
          </IconButton>
        </>
      ) : (
        <Box />
      )}

      {/* ── PROFILE ──────────────────────────────────────────────── */}
      <Box display="flex">
        <IconButton
          onClick={(e) => setProfileAnchorEl(e.currentTarget)}
          sx={{ color: "rgba(186,230,253,0.85)" }}
        >
          <PersonOutlinedIcon />
        </IconButton>

        <Popover
          open={openProfileMenu}
          anchorEl={profileAnchorEl}
          onClose={() => setProfileAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              background: "linear-gradient(180deg, #091b3d 0%, #060f24 100%)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "14px",
              minWidth: 180,
              mt: 0.5,
            },
          }}
        >
          <Box
            px={2}
            pt={2}
            pb={1}
            sx={{ cursor: "pointer" }}
            onClick={() => navigate("/profile")}
          >
            <Typography
              sx={{
                ...poppins,
                fontSize: "0.72rem",
                color: "rgba(186,230,253,0.5)",
                fontWeight: 500,
              }}
            >
              Logged in as
            </Typography>
            <Typography
              sx={{
                ...poppins,
                fontSize: "0.9rem",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              {user.name}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
          <Box px={1} py={1}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: "10px",
                px: 1.5,
                py: 1,
                "&:hover": { backgroundColor: "rgba(239,68,68,0.1)" },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 32, color: "rgba(248,113,113,0.8)" }}
              >
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  sx: {
                    ...poppins,
                    fontSize: "0.85rem",
                    color: "rgba(248,113,113,0.8)",
                    fontWeight: 500,
                  },
                }}
              />
            </ListItemButton>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
}
